import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn, localToday } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  BarChart3,
  TrendingUp,
  Users,
  DollarSign,
  Download,
  Loader2,
  Calendar,
  CheckCircle2,
  Briefcase,
  Tag,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Import extracted components
import { StatCard, WorkloadComparisonChart, SimpleBarChart } from '@/components/reports';

const STATUS_COLORS = {
  new: '#6B7280',
  confirmed: '#3B82F6',
  in_progress: '#F59E0B',
  done: '#10B981',
  cancelled: '#EF4444',
  unpaid: '#EF4444',
  partial: '#F59E0B',
  paid: '#10B981',
  refund: '#8B5CF6'
};

export default function ReportsPage() {
  const { api } = useAuth();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    return localToday(new Date(d.getFullYear(), d.getMonth(), 1));
  });
  const [dateTo, setDateTo] = useState(() => localToday());
  
  const [overview, setOverview] = useState(null);
  const [ordersByDate, setOrdersByDate] = useState([]);
  const [workersPerf, setWorkersPerf] = useState([]);
  const [servicesPerf, setServicesPerf] = useState([]);
  const [ordersByStatus, setOrdersByStatus] = useState(null);
  const [workloadComparison, setWorkloadComparison] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, byDateRes, workersRes, servicesRes, statusRes, workloadRes] = await Promise.all([
        api.get(`/reports/overview?date_from=${dateFrom}&date_to=${dateTo}`),
        api.get(`/reports/orders-by-date?date_from=${dateFrom}&date_to=${dateTo}`),
        api.get(`/reports/workers-performance?date_from=${dateFrom}&date_to=${dateTo}`),
        api.get(`/reports/services-performance?date_from=${dateFrom}&date_to=${dateTo}`),
        api.get(`/reports/orders-by-status?date_from=${dateFrom}&date_to=${dateTo}`),
        api.get(`/reports/workload-comparison?date_from=${dateFrom}&date_to=${dateTo}`)
      ]);
      
      setOverview(overviewRes.data);
      setOrdersByDate(byDateRes.data);
      setWorkersPerf(workersRes.data);
      setServicesPerf(servicesRes.data);
      setOrdersByStatus(statusRes.data);
      setWorkloadComparison(workloadRes.data);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [api, dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    try {
      const response = await api.get(`/reports/export/summary.csv?date_from=${dateFrom}&date_to=${dateTo}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${dateFrom}_${dateTo}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('time.exportSuccess'));
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const handleExportXlsx = async () => {
    try {
      const response = await api.get(`/reports/orders/export.xlsx?date_from=${dateFrom}&date_to=${dateTo}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orders_${dateFrom}_${dateTo}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('time.exportSuccess'));
    } catch (err) {
      toast.error('XLSX export failed');
    }
  };

  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      
      doc.setFontSize(18);
      doc.text('Analytics Report', 14, 22);
      
      doc.setFontSize(11);
      doc.text(`Period: ${dateFrom} - ${dateTo}`, 14, 32);
      
      doc.setFontSize(12);
      doc.text('Overview', 14, 44);
      doc.setFontSize(10);
      doc.text(`Total Orders: ${overview?.orders?.total || 0}`, 14, 52);
      doc.text(`Completion Rate: ${overview?.orders?.completion_rate || 0}%`, 14, 58);
      doc.text(`Total Revenue: $${(overview?.revenue?.total || 0).toFixed(0)}`, 14, 64);
      doc.text(`Active Workers: ${overview?.workers?.active || 0}`, 14, 70);
      doc.text(`Total Hours: ${(overview?.workers?.total_hours || 0).toFixed(0)}`, 14, 76);
      
      if (workersPerf.length > 0) {
        doc.setFontSize(12);
        doc.text('Workers Performance', 14, 90);
        
        const workersData = workersPerf.map(w => [
          w.worker_name || 'Unknown',
          w.orders_total || 0,
          w.orders_completed || 0,
          `${w.completion_rate || 0}%`,
          (w.hours_worked || 0).toFixed(1),
          `$${(w.revenue || 0).toFixed(0)}`
        ]);
        
        autoTable(doc, {
          startY: 95,
          head: [['Worker', 'Orders', 'Completed', 'Rate', 'Hours', 'Revenue']],
          body: workersData,
          theme: 'striped',
          headStyles: { fillColor: [79, 70, 229] },
          styles: { fontSize: 9 }
        });
      }
      
      if (servicesPerf.length > 0) {
        const finalY = doc.lastAutoTable?.finalY || 95;
        doc.setFontSize(12);
        doc.text('Services Performance', 14, finalY + 15);
        
        const servicesData = servicesPerf.map(s => [
          s.service_name || 'Unknown',
          s.orders_total || 0,
          s.orders_completed || 0,
          `$${(s.revenue || 0).toFixed(0)}`
        ]);
        
        autoTable(doc, {
          startY: finalY + 20,
          head: [['Service', 'Orders', 'Completed', 'Revenue']],
          body: servicesData,
          theme: 'striped',
          headStyles: { fillColor: [16, 185, 129] },
          styles: { fontSize: 9 }
        });
      }
      
      const lastY = doc.lastAutoTable?.finalY || 150;
      doc.setFontSize(9);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, lastY + 15);
      
      doc.save(`report_${dateFrom}_${dateTo}.pdf`);
      toast.success(t('time.exportSuccess'));
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error('PDF export failed');
    }
  };

  const getStatusLabel = (status) => {
    return t(`status.${status}`) || status;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">{t('reports.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('reports.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="export-report-btn">
            <Download className="h-4 w-4 mr-2" />
            CSV
          </Button>
          <Button variant="outline" onClick={handleExportPDF} data-testid="export-report-pdf-btn">
            <FileText className="h-4 w-4 mr-2" />
            PDF
          </Button>
          <Button variant="outline" onClick={handleExportXlsx} data-testid="export-report-xlsx-btn">
            <Download className="h-4 w-4 mr-2" />
            XLSX
          </Button>
        </div>
      </div>

      {/* Period Filter */}
      <Card className="cgc-card">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm">{t('reports.period')}:</Label>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-[150px]"
                data-testid="report-date-from"
              />
              <span className="text-muted-foreground">—</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-[150px]"
                data-testid="report-date-to"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      ) : (
        <>
          {/* Overview Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              icon={Briefcase}
              label={t('reports.totalOrders')}
              value={overview?.orders?.total || 0}
              subValue={`${overview?.orders?.completion_rate || 0}% ${t('reports.completionRate')}`}
            />
            <StatCard
              icon={DollarSign}
              label={t('reports.totalRevenue')}
              value={`$${(overview?.revenue?.total || 0).toFixed(0)}`}
              subValue={`$${(overview?.revenue?.avg_order_value || 0).toFixed(0)} ${t('reports.avgOrderValue')}`}
            />
            <StatCard
              icon={Users}
              label={t('reports.activeWorkers')}
              value={overview?.workers?.active || 0}
              subValue={`${(overview?.workers?.total_hours || 0).toFixed(0)} ${t('reports.totalHours')}`}
            />
            <StatCard
              icon={TrendingUp}
              label={t('reports.newClients')}
              value={overview?.clients?.new || 0}
            />
          </div>

          {/* Tabs for different views */}
          <Tabs defaultValue="workers" className="space-y-4">
            <TabsList className="flex flex-wrap gap-1 h-auto p-1">
              <TabsTrigger value="workers" data-testid="tab-workers" className="text-xs sm:text-sm">{t('reports.byWorker')}</TabsTrigger>
              <TabsTrigger value="workload" data-testid="tab-workload" className="text-xs sm:text-sm">{t('reports.workload')}</TabsTrigger>
              <TabsTrigger value="services" data-testid="tab-services" className="text-xs sm:text-sm">{t('reports.byService')}</TabsTrigger>
              <TabsTrigger value="status" data-testid="tab-status" className="text-xs sm:text-sm">{t('reports.byStatus')}</TabsTrigger>
            </TabsList>

            {/* Workers Performance */}
            <TabsContent value="workers">
              <Card className="cgc-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    {t('reports.workers')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {workersPerf.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">{t('reports.noData')}</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('reports.workerName')}</TableHead>
                            <TableHead className="text-center">{t('reports.ordersCount')}</TableHead>
                            <TableHead className="text-center">{t('reports.ordersCompleted')}</TableHead>
                            <TableHead className="text-center">{t('reports.hoursWorked')}</TableHead>
                            <TableHead className="text-right">{t('reports.revenue')}</TableHead>
                            <TableHead className="text-right">{t('reports.revenuePerHour')}</TableHead>
                            <TableHead className="text-right">Прибыль/час</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {workersPerf.map((w, idx) => {
                            const workerName = w.worker_name || w.name;
                            const revenue = w.revenue || 0;
                            const hoursWorked = w.hours_worked || 0;
                            const workerPay = w.worker_pay || 0;
                            const revenuePerHour = hoursWorked > 0 ? revenue / hoursWorked : 0;
                            const profitPerHour = hoursWorked > 0 ? (revenue - workerPay) / hoursWorked : 0;
                            const ordersTotal = w.orders_total || 0;
                            const ordersCompleted = w.orders_completed || 0;
                            const completionRate = w.completion_rate ?? (ordersTotal > 0 ? Math.round((ordersCompleted / ordersTotal) * 100) : 0);
                            
                            return (
                            <TableRow key={idx} data-testid={`worker-row-${idx}`}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-3 h-3 rounded-full" 
                                    style={{ backgroundColor: w.color }}
                                  />
                                  <span className="font-medium">{workerName}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">{ordersTotal}</TableCell>
                              <TableCell className="text-center">
                                <span className="text-green-600">{ordersCompleted}</span>
                                <span className="text-muted-foreground text-xs ml-1">
                                  ({completionRate}%)
                                </span>
                              </TableCell>
                              <TableCell className="text-center tabular-nums">{hoursWorked}</TableCell>
                              <TableCell className="text-right font-mono font-medium">${revenue.toFixed(0)}</TableCell>
                              <TableCell className="text-right font-mono text-muted-foreground">${revenuePerHour.toFixed(0)}</TableCell>
                              <TableCell className={cn("text-right font-mono font-medium", profitPerHour >= 0 ? "text-green-600" : "text-red-500")}>${profitPerHour.toFixed(0)}</TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Workload Comparison */}
            <TabsContent value="workload">
              <Card className="cgc-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    {t('reports.workloadComparison')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <WorkloadComparisonChart data={workloadComparison} t={t} />
                  
                  {workloadComparison.length > 0 && (
                    <div className="mt-6 overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('reports.workerName')}</TableHead>
                            <TableHead className="text-center">{t('reports.availableCapacity')}</TableHead>
                            <TableHead className="text-center">{t('reports.scheduledHours')}</TableHead>
                            <TableHead className="text-center">{t('reports.workedHours')}</TableHead>
                            <TableHead className="text-center">{t('reports.utilization')}</TableHead>
                            <TableHead className="text-center">{t('reports.efficiency')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {workloadComparison.map((w, idx) => (
                            <TableRow key={idx} data-testid={`workload-row-${idx}`}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-3 h-3 rounded-full" 
                                    style={{ backgroundColor: w.color }}
                                  />
                                  <span className="font-medium">{w.worker_name}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-center tabular-nums">{w.available_capacity}h</TableCell>
                              <TableCell className="text-center tabular-nums text-amber-600">{w.scheduled_hours}h</TableCell>
                              <TableCell className="text-center tabular-nums text-green-600">{w.worked_hours}h</TableCell>
                              <TableCell className="text-center">
                                <Badge variant={w.utilization >= 80 ? "default" : w.utilization >= 50 ? "secondary" : "outline"}>
                                  {w.utilization}%
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge variant={w.efficiency >= 90 ? "default" : w.efficiency >= 70 ? "secondary" : "outline"}>
                                  {w.efficiency}%
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Services Performance */}
            <TabsContent value="services">
              <Card className="cgc-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Tag className="h-5 w-5" />
                    {t('reports.services')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {servicesPerf.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">{t('reports.noData')}</p>
                  ) : (
                    <div className="space-y-6">
                      <SimpleBarChart 
                        data={servicesPerf}
                        valueKey="revenue"
                        labelKey="service_name"
                        colorKey="color"
                      />
                      <div className="overflow-x-auto pt-4 border-t">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{t('reports.serviceName')}</TableHead>
                              <TableHead className="text-center">{t('reports.ordersCount')}</TableHead>
                              <TableHead className="text-center">{t('reports.ordersCompleted')}</TableHead>
                              <TableHead className="text-right">{t('reports.revenue')}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {servicesPerf.map((s, idx) => {
                              const serviceName = s.service_name || s.name;
                              const ordersTotal = s.orders_total || s.orders_count || 0;
                              const ordersCompleted = s.orders_completed ?? ordersTotal;
                              const revenue = s.revenue || 0;
                              
                              return (
                              <TableRow key={idx}>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <div 
                                      className="w-3 h-3 rounded-full" 
                                      style={{ backgroundColor: s.color }}
                                    />
                                    <span className="font-medium">{serviceName}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">{ordersTotal}</TableCell>
                                <TableCell className="text-center text-green-600">{ordersCompleted}</TableCell>
                                <TableCell className="text-right font-mono font-medium">${revenue.toFixed(0)}</TableCell>
                              </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Status Breakdown */}
            <TabsContent value="status">
              <div className="grid md:grid-cols-2 gap-4">
                {/* Operational Status */}
                <Card className="cgc-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <CheckCircle2 className="h-5 w-5" />
                      {t('status.operationalTitle')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {ordersByStatus?.operational_status ? (
                      <div className="space-y-3">
                        {Object.entries(ordersByStatus.operational_status).map(([status, count]) => (
                          <div key={status} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: STATUS_COLORS[status] || '#6B7280' }}
                              />
                              <span className="text-sm">{getStatusLabel(status)}</span>
                            </div>
                            <Badge variant="outline" className="tabular-nums">{count}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4">{t('reports.noData')}</p>
                    )}
                  </CardContent>
                </Card>

                {/* Payment Status */}
                <Card className="cgc-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <DollarSign className="h-5 w-5" />
                      {t('status.paymentTitle')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {ordersByStatus?.payment_status ? (
                      <div className="space-y-3">
                        {Object.entries(ordersByStatus.payment_status).map(([status, count]) => (
                          <div key={status} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: STATUS_COLORS[status] || '#6B7280' }}
                              />
                              <span className="text-sm">{getStatusLabel(status)}</span>
                            </div>
                            <Badge variant="outline" className="tabular-nums">{count}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4">{t('reports.noData')}</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
